import type { ReportMedia } from '../types/api';

/**
 * Turns a report key into a readable label, splitting camelCase and snake_case
 * alike: `avgNetProfit` / `avg_net_profit` → "Avg Net Profit".
 */
export function humanizeKey(key: string): string {
    return key
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2') // camelCase boundary
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2') // ACRONYMWord boundary
        .split(/[._\s]+/)
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
}

/** Renders a single primitive report value as a readable string. */
export function formatReportValue(value: unknown): string {
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
    }
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }
    if (typeof value === 'string') {
        /**
         * Prettify only enum-like identifiers: snake_case / dotted words, or
         * camelCase. Numbers like "3444.00" and free text are left untouched so
         * decimals aren't mangled into "3444 00".
         */
        const isSnakeOrDotted = /^[a-zA-Z]+([._][a-zA-Z]+)+$/.test(value);
        const isCamelCase = /^[a-z]+([A-Z][a-z0-9]*)+$/.test(value);
        return isSnakeOrDotted || isCamelCase ? humanizeKey(value) : value;
    }
    return String(value);
}

export type FlatField = { label: string; value: string };

/**
 * Display labels for findings keys whose humanized name reads poorly. Keyed by
 * the leaf field name from the field officer report (`report.data.findings.*`).
 */
const LABEL_OVERRIDES: Record<string, string> = {
    notes: 'Agent comment',
    addressUnknownReason: 'Reason address not confirmed',
    lga: 'LGA',
};

/**
 * Flattens a report's free-form `data` JSONB into labeled rows so nested
 * objects render as readable fields instead of raw JSON. Only the leaf field
 * name is shown as the label.
 */
export function flattenReportData(value: unknown, path: string[] = []): FlatField[] {
    const leaf = path.length ? path[path.length - 1] : '';
    // Repeated blocks (e.g. `units[]`) carry their 1-based index in the path;
    // surface it so three assets don't render as three identical labels.
    const index = path.length > 1 && /^\d+$/.test(path[path.length - 2]) ? path[path.length - 2] : '';
    const base = leaf ? (LABEL_OVERRIDES[leaf] ?? humanizeKey(leaf)) : '';
    const label = index ? `${index}. ${base}` : base;

    if (value === null || value === undefined) {
        return path.length ? [{ label, value: '—' }] : [];
    }

    if (Array.isArray(value)) {
        if (value.length === 0) {
            return [{ label, value: '—' }];
        }
        const allPrimitive = value.every((v) => v === null || typeof v !== 'object');
        if (allPrimitive) {
            return [{ label, value: value.map(formatReportValue).join(', ') }];
        }
        return value.flatMap((item, i) => flattenReportData(item, [...path, String(i + 1)]));
    }

    if (typeof value === 'object') {
        return Object.entries(value as Record<string, unknown>).flatMap(([k, v]) =>
            flattenReportData(v, [...path, k]),
        );
    }

    return [{ label, value: formatReportValue(value) }];
}

/**
 * Pulls a displayable GPS string and a location URL out of a media item's
 * free-form `metadata`. Tolerant of key shapes (`gps`, `latitude`/`longitude`,
 * `lat`/`lng`, any `*url*` http link); derives a Google Maps URL from the
 * coordinates when the app didn't send an explicit one.
 */
export function mediaGeo(metadata?: Record<string, unknown> | null): {
    gps?: string;
    url?: string;
} {
    const m = metadata ?? {};
    const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
    const coord = (v: unknown) => (typeof v === 'number' ? String(v) : str(v));

    // URL: any key containing "url" whose value is an http(s) link.
    let url: string | undefined;
    for (const [k, v] of Object.entries(m)) {
        if (/url/i.test(k) && typeof v === 'string' && /^https?:\/\//.test(v)) {
            url = v;
            break;
        }
    }

    const lat = coord(m.latitude ?? m.lat);
    const lng = coord(m.longitude ?? m.lng ?? m.lon ?? m.long);
    let gps = str(m.gps) ?? str(m.location) ?? str(m.coordinates);
    if (!gps && lat && lng) {
        gps = `${lat}, ${lng}`;
    }
    if (!url && lat && lng) {
        url = `https://www.google.com/maps?q=${lat},${lng}`;
    }

    return { gps, url };
}

/** True when a media item is an image we can render inline. */
export function isImageMedia(media: ReportMedia): boolean {
    return (
        media.mimeType?.startsWith('image/') === true ||
        media.mediaType?.toLowerCase() === 'photo' ||
        media.mediaType?.toLowerCase() === 'image'
    );
}

/** The label shown above a photo / next to an attachment link. */
export function mediaLabel(media: ReportMedia): string {
    const raw = media.metadata?.label;
    return (typeof raw === 'string' && raw.trim()) || humanizeKey(media.mediaType);
}
